import React from "react";
import { useShallow } from "zustand/react/shallow";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { ListCount } from "@/components/main/pages/pageColumn";
import {
  useOrgContextStore,
  visibleSpaces,
} from "@/components/main/stores/orgContextStore";

interface CreateModelStripProps {
  /// Org-wide, not the filtered bucket: the button only pulses for an account
  /// that has never made a model.
  hasModels: boolean;
  /// The naming overlay is open, so the button reads as pressed.
  isNaming: boolean;
  /// The focused scope's models, counted at the line's start as the other
  /// list pages count theirs.
  count: number;
  onNewModel: () => void;
}

/// The Overview's create action. It opens the naming overlay; where the model
/// goes is the overlay's footer, which asks when no space is focused.
export const CreateModelStrip: React.FC<CreateModelStripProps> = ({
  hasModels,
  isNaming,
  count,
  onNewModel,
}) => {
  const { activeOrg, hasSpaces } = useOrgContextStore(
    useShallow((s) => ({
      activeOrg: s.activeOrg(),
      hasSpaces: visibleSpaces(s.spaces).length > 0,
    })),
  );
  // The door refuses without a live space, so the affordance says so instead
  // of failing after the click. An org on its way out takes nothing new.
  const refusalReason = activeOrg?.deletion_scheduled_at
    ? `${activeOrg.name} is scheduled for deletion — it takes no new models until that is cancelled.`
    : hasSpaces
      ? undefined
      : "Create a space first — models live in spaces.";

  return (
    // The rows' px-1: the count lines up with the section above, the button with its rule's end.
    <div className="flex items-center mt-8 px-1">
      <ListCount
        parts={[count > 0 && `${count} ${count === 1 ? "model" : "models"}`]}
      />
      <span
        className={`ml-auto flex${!hasModels && !refusalReason ? " pulse-hint" : ""}`}
      >
        <MetaAddButton
          label="New model"
          className="naming-opener"
          onClick={onNewModel}
          disabled={!!refusalReason}
          title={refusalReason}
          ariaHasPopup="dialog"
          ariaExpanded={isNaming}
        />
      </span>
    </div>
  );
};
